import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import LegalPageLayout from '@/components/LegalPageLayout';
import { useAcceptanceLog } from '@/hooks/useAcceptanceLog';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { logAcceptance } = useAcceptanceLog();

  const handleAccept = () => {
    logAcceptance('privacyPolicy', 'a Política de Privacidade');
    navigate('/');
  };

  return (
    <>
      <Helmet>
        <title>Política de Privacidade | Aceena</title>
        <meta name="description" content="Política de Privacidade da Aceena. Saiba como coletamos, usamos e protegemos seus dados pessoais." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <LegalPageLayout
        title="Política de Privacidade"
        lastUpdated="22 de abril de 2025"
        onAccept={handleAccept}
      >
        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">1. Introdução</h2>
          <p className="text-foreground/80 leading-relaxed">
            A Aceena Tecnologia e Consultoria Automotiva Ltda. ("Aceena", "nós", "nosso") respeita a privacidade dos usuários ("você", "seu") e está comprometida em proteger suas informações pessoais. Esta Política de Privacidade explica como coletamos, usamos, compartilhamos e protegemos suas informações quando você utiliza nossos serviços.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">2. Informações que Coletamos</h2>
          <p className="text-foreground/80 leading-relaxed mb-4">Podemos coletar os seguintes tipos de informações:</p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li><strong>Informações de Identificação Pessoal:</strong> Nome, endereço de e-mail, número de telefone, CPF, endereço residencial e outros dados necessários para a prestação de nossos serviços.</li>
            <li><strong>Informações de Pagamento:</strong> Dados de cartão de crédito ou débito, informações de conta bancária e histórico de transações.</li>
            <li><strong>Informações de Uso:</strong> Dados sobre como você interage com nossos serviços, incluindo páginas visitadas, tempo gasto em cada página e ações realizadas.</li>
            <li><strong>Informações do Dispositivo:</strong> Tipo de dispositivo, sistema operacional, identificadores únicos e dados de rede.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">3. Como Usamos Suas Informações</h2>
          <p className="text-foreground/80 leading-relaxed mb-4">Utilizamos suas informações para:</p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li>Fornecer, manter e melhorar nossos serviços.</li>
            <li>Processar transações e enviar notificações relacionadas.</li>
            <li>Personalizar sua experiência e oferecer conteúdo relevante.</li>
            <li>Comunicar-nos com você sobre atualizações, ofertas e promoções.</li>
            <li>Garantir a segurança e integridade de nossos serviços.</li>
            <li>Cumprir obrigações legais e regulatórias.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">4. Compartilhamento de Informações</h2>
          <p className="text-foreground/80 leading-relaxed mb-4">Podemos compartilhar suas informações com:</p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li><strong>Parceiros de Negócios:</strong> Locadoras de veículos e outras empresas parceiras para a prestação de nossos serviços.</li>
            <li><strong>Prestadores de Serviços:</strong> Empresas que nos auxiliam em operações como processamento de pagamentos, análise de dados e suporte ao cliente.</li>
            <li><strong>Autoridades Legais:</strong> Quando exigido por lei ou para proteger nossos direitos legais.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">5. Segurança das Informações</h2>
          <p className="text-foreground/80 leading-relaxed">
            Implementamos medidas de segurança técnicas e organizacionais para proteger suas informações contra acesso não autorizado, alteração, divulgação ou destruição. No entanto, nenhum método de transmissão ou armazenamento eletrônico é 100% seguro.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">6. Seus Direitos</h2>
          <p className="text-foreground/80 leading-relaxed mb-4">Você tem o direito de:</p>
          <ul className="list-disc pl-6 space-y-2 text-foreground/80">
            <li>Acessar suas informações pessoais que mantemos.</li>
            <li>Solicitar a correção de informações imprecisas.</li>
            <li>Solicitar a exclusão de suas informações, sujeito a obrigações legais.</li>
            <li>Opor-se ao processamento de suas informações para fins de marketing.</li>
            <li>Solicitar a portabilidade de suas informações.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">7. Retenção de Dados</h2>
          <p className="text-foreground/80 leading-relaxed">
            Mantemos suas informações pessoais pelo tempo necessário para cumprir as finalidades descritas nesta política, a menos que um período de retenção mais longo seja exigido ou permitido por lei.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">8. Cookies e Tecnologias Semelhantes</h2>
          <p className="text-foreground/80 leading-relaxed">
            Utilizamos cookies e tecnologias semelhantes para melhorar sua experiência, analisar o tráfego do site e personalizar conteúdo. Você pode gerenciar suas preferências de cookies através das configurações do seu navegador.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">9. Alterações nesta Política</h2>
          <p className="text-foreground/80 leading-relaxed">
            Podemos atualizar esta Política de Privacidade periodicamente. Notificaremos você sobre quaisquer alterações significativas publicando a nova política em nosso site e atualizando a data de "Última atualização".
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-4">10. Contato</h2>
          <p className="text-foreground/80 leading-relaxed">
            Se você tiver dúvidas ou preocupações sobre esta Política de Privacidade, entre em contato conosco pelo e-mail{' '}
            <a href="mailto:contato@aceena.com.br" className="text-aceena-green hover:underline">
              contato@aceena.com.br
            </a>
            {' '}ou pelo telefone{' '}
            <a href="tel:+5511989004165" className="text-aceena-green hover:underline">
              (11) 98900-4165
            </a>.
          </p>
        </section>
      </LegalPageLayout>
    </>
  );
};

export default PrivacyPolicy;
